import React from 'react';
import { useTranslation } from 'react-i18next';

import { ROUTES, PROFILE_ROUTES } from 'configs';

import * as Ch from 'pages/Subscriptions/SubscriptionInner/styled';
import * as O from './StripeCheckoutOutcome.styled';

export type StripeCheckoutOutcomeProps = {
  variant: 'success' | 'failure';
  /** Stripe Checkout Session id (cs_…) — shown truncated when present */
  sessionId?: string | null;
  planLabel?: string;
  onDismiss: () => void;
};

const truncateSessionId = (id: string, max = 24) => {
  if (id.length <= max) return id;
  return `${id.slice(0, 14)}…${id.slice(-8)}`;
};

const StripeCheckoutOutcome = ({
  variant,
  sessionId,
  planLabel,
  onDismiss,
}: StripeCheckoutOutcomeProps) => {
  const { t } = useTranslation();

  const settingsPath = `${ROUTES.PROFILE}/${PROFILE_ROUTES.SETTINGS}`;
  const subscriptionPath = `${ROUTES.PROFILE}/${PROFILE_ROUTES.SUBSCRIPTION}`;
  const changePlanPath = `${ROUTES.PROFILE}/${PROFILE_ROUTES.SUBSCRIPTION}/${PROFILE_ROUTES.CHANGE_PLAN}`;

  const breadcrumbPlan =
    variant === 'success'
      ? t('subscriptions.checkout.stripeReturnBreadcrumbSuccess')
      : t('subscriptions.checkout.stripeReturnBreadcrumbFailure');

  return (
    <Ch.CheckoutBackdrop>
      <O.OutcomePageWrap>
        <Ch.CheckoutBreadcrumb aria-label="Breadcrumb">
          <Ch.CheckoutBreadcrumbLink to={settingsPath}>
            {t('changePlan.breadcrumbSettings')}
          </Ch.CheckoutBreadcrumbLink>
          <Ch.CheckoutBreadcrumbSep aria-hidden>›</Ch.CheckoutBreadcrumbSep>
          <Ch.CheckoutBreadcrumbLink to={subscriptionPath}>
            {t('changePlan.breadcrumbSubscription')}
          </Ch.CheckoutBreadcrumbLink>
          <Ch.CheckoutBreadcrumbSep aria-hidden>›</Ch.CheckoutBreadcrumbSep>
          <Ch.CheckoutBreadcrumbCurrent>
            {breadcrumbPlan}
          </Ch.CheckoutBreadcrumbCurrent>
        </Ch.CheckoutBreadcrumb>

        <Ch.ProgressBar>
          <Ch.ProgStep $doneConnector>
            <Ch.ProgDot $done aria-hidden>
              ✓
            </Ch.ProgDot>
            <Ch.ProgLabel $done>
              {t('subscriptions.checkout.stepPlanSelected')}
            </Ch.ProgLabel>
          </Ch.ProgStep>
          <Ch.ProgStep $doneConnector>
            <Ch.ProgDot $done aria-hidden>
              ✓
            </Ch.ProgDot>
            <Ch.ProgLabel $done>
              {t('subscriptions.checkout.stepOrderSummary')}
            </Ch.ProgLabel>
          </Ch.ProgStep>
          <Ch.ProgStep>
            <Ch.ProgDot $active>3</Ch.ProgDot>
            <Ch.ProgLabel $active>
              {t('subscriptions.checkout.stepConfirmation')}
            </Ch.ProgLabel>
          </Ch.ProgStep>
        </Ch.ProgressBar>

        {variant === 'success' ? (
          <O.SuccessWrap>
            <O.SuccessCircle aria-hidden>🎉</O.SuccessCircle>
            <O.SuccessTitle>
              {t('subscriptions.checkout.stripeReturnSuccessTitle')}
            </O.SuccessTitle>
            <O.SuccessSub>
              {t('subscriptions.checkout.stripeReturnSuccessSub', {
                plan:
                  planLabel ||
                  t('subscriptions.checkout.stripeReturnPlanFallback'),
              })}
            </O.SuccessSub>
            <O.SuccessDetails>
              <O.SuccessDetailsHeading>
                {t('subscriptions.checkout.stripeReturnSuccessDetailsHeading')}
              </O.SuccessDetailsHeading>
              <O.SuccessList>
                <O.SuccessListItem>
                  <O.SuccessCheck>✓</O.SuccessCheck>
                  {t('subscriptions.checkout.stripeReturnSuccessBulletPlan', {
                    plan:
                      planLabel ||
                      t('subscriptions.checkout.stripeReturnPlanFallback'),
                  })}
                </O.SuccessListItem>
                <O.SuccessListItem>
                  <O.SuccessCheck>✓</O.SuccessCheck>
                  {t('subscriptions.checkout.stripeReturnSuccessBulletActive')}
                </O.SuccessListItem>
                <O.SuccessListItem>
                  <O.SuccessCheck>✓</O.SuccessCheck>
                  {t('subscriptions.checkout.stripeReturnSuccessBulletEmail')}
                </O.SuccessListItem>
                <O.SuccessListItem>
                  <O.SuccessCheck>✓</O.SuccessCheck>
                  {t(
                    'subscriptions.checkout.stripeReturnSuccessBulletGuarantee',
                  )}
                </O.SuccessListItem>
                {sessionId ? (
                  <O.SuccessListItem>
                    <O.SuccessCheck aria-hidden> </O.SuccessCheck>
                    <span style={{ fontSize: 12, color: '#94a3b8' }}>
                      {t('subscriptions.checkout.stripeReturnSessionRef', {
                        id: truncateSessionId(sessionId),
                      })}
                    </span>
                  </O.SuccessListItem>
                ) : null}
              </O.SuccessList>
            </O.SuccessDetails>
            <O.SuccessBtns>
              <O.BtnPrimaryLink to={ROUTES.HOME_PAGE} replace>
                {t('subscriptions.checkout.stripeReturnGoHome')}
              </O.BtnPrimaryLink>
              <O.BtnSecondaryOutcome type="button" onClick={onDismiss}>
                {t('subscriptions.checkout.stripeReturnViewSubscription')}
              </O.BtnSecondaryOutcome>
            </O.SuccessBtns>
          </O.SuccessWrap>
        ) : (
          <O.SuccessWrap>
            <O.FailureCircle aria-hidden>✕</O.FailureCircle>
            <O.FailureTitle>
              {t('subscriptions.checkout.stripeReturnFailureTitle')}
            </O.FailureTitle>
            <O.FailureSub>
              {t('subscriptions.checkout.stripeReturnFailureSub')}
            </O.FailureSub>
            <O.SuccessDetails>
              <O.SuccessDetailsHeading>
                {t('subscriptions.checkout.stripeReturnFailureDetailsHeading')}
              </O.SuccessDetailsHeading>
              <O.SuccessList>
                <O.SuccessListItem>
                  <O.SuccessCheck style={{ color: '#e24b4a' }}>
                    !
                  </O.SuccessCheck>
                  {t('subscriptions.checkout.stripeReturnFailureBullet1')}
                </O.SuccessListItem>
                <O.SuccessListItem>
                  <O.SuccessCheck style={{ color: '#e24b4a' }}>
                    !
                  </O.SuccessCheck>
                  {t('subscriptions.checkout.stripeReturnFailureBullet2')}
                </O.SuccessListItem>
              </O.SuccessList>
            </O.SuccessDetails>
            <O.SuccessBtns>
              <O.BtnPrimaryLink to={changePlanPath} replace onClick={onDismiss}>
                {t('subscriptions.checkout.stripeReturnTryAgain')}
              </O.BtnPrimaryLink>
              <O.BtnSecondaryOutcome type="button" onClick={onDismiss}>
                {t('subscriptions.checkout.stripeReturnBackSubscription')}
              </O.BtnSecondaryOutcome>
            </O.SuccessBtns>
          </O.SuccessWrap>
        )}
      </O.OutcomePageWrap>
    </Ch.CheckoutBackdrop>
  );
};

export default StripeCheckoutOutcome;
